import type { ComponentProps } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider, FormControlLabel,
  InputAdornment, List, ListItemButton, ListItemText, Stack, Switch, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import SearchIcon from '@mui/icons-material/Search';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';

import { api } from '../api/client';
import type { Category, City, Covering, PlaceTally, Poi } from '../api/types';
import { COVERED_COLOUR } from '../theme';
import type { ViewBox } from '../util/viewBox';
import { holds, wider } from '../util/viewBox';
import { MapView } from './MapView';
import { PlaceCoverageReport, onDay } from './PlaceCoverageReport';
import { PlaceDetailsBody, usePlaceDetails } from './PlaceDetailsBody';

/*
 * Choosing where a round goes, with the record in front of you.
 *
 * The form's pickers answer "which place did you mean?" — you type a name and
 * take what comes back. They cannot answer the question somebody actually has
 * while building a round: of the places that would do, which has already been
 * worked, by whom, and how recently?
 *
 * So this is a browser rather than a picker. The catalogue on the left,
 * narrowed by kind, by name and by whether anything has been planned there;
 * the map or the coverage record on the right; and the three things a place
 * can be to a round along the bottom. Nothing is decided until one of those
 * is pressed, so a place can be read about and passed over, which is the
 * point.
 *
 * The body of it lives here rather than in either of its two homes: it opens
 * over the planner as a window while a round is being built, and stands on
 * its own page for reading the city with no round in hand. The same browser
 * in both, so what it says in one is what it says in the other — only the
 * three actions differ, because a page has no round to add anything to.
 */

/** How many rows are fetched at a time, and again whenever more is asked. */
const PAGE = 50;

/**
 * How many marked places the map will carry at once.
 *
 * Each is a DOM node with a tooltip, and they are drawn for the ground in
 * view rather than for the whole city — so this is a ceiling on one
 * screenful, which zooming out raises the pressure on and zooming in
 * relieves. Past it the heaviest come first, so what is cut is ground
 * planned once rather than ground planned eight times. The round's own
 * points and whatever is being read about are added after the cut and
 * never fall off it.
 */
const MAP_PINS = 250;

/** One marker, as the map defines them. */
type Marker = NonNullable<ComponentProps<typeof MapView>['pins']>[number];

/** Which part of the catalogue is being looked at. */
type Standing = 'all' | 'new' | 'planned';

/**
 * One line of the list.
 *
 * Less than a place, because the two sides of this list know different
 * amounts: a row read from the catalogue is a whole Poi, while a row of
 * ground already worked is whatever the tally carries. Both show the same
 * three things, and picking either fetches the rest.
 */
type Row = { id: string; name: string; categoryLabel: string };

export function PlaceBrowser({
  city, categories, basemapUrl, active = true, plannedOnly = false,
  onSetStart, onSetEnd, onAddStop, inRound = [], startId, endId,
}: {
  city: City;
  /** The city's own categories, for the filter. */
  categories: Category[];
  /** The tiles the page's own map draws, for this one's map view. */
  basemapUrl?: string | null;
  /**
   * False while the browser is put away, which stops it reading the
   * catalogue and lets go of whatever was picked. A page that is simply on
   * screen leaves this alone.
   */
  active?: boolean;
  /**
   * Ground already planned, and no way to the rest of the catalogue.
   *
   * The coverage page asks one question — what has been worked, and how
   * heavily — and the eight thousand places nobody has planned are not an
   * answer to it. Inside the planner the browser keeps all three lists,
   * because there the point is choosing from everything.
   */
  plannedOnly?: boolean;
  /**
   * What a place can be made into, where there is a round to make it part
   * of. All three absent — the standalone page — and the browser is for
   * reading rather than deciding, so the foot of it is not drawn.
   */
  onSetStart?: (poi: Poi) => void;
  onSetEnd?: (poi: Poi) => void;
  onAddStop?: (poi: Poi) => void;
  /** Ids already named in the round, so the list can say so. */
  inRound?: string[];
  startId?: string | undefined;
  endId?: string | undefined;
}) {
  const [query, setQuery] = useState('');
  const [kinds, setKinds] = useState<Category[]>([]);
  const [chosen, setChosen] = useState<Standing>('all');
  const standing: Standing = plannedOnly ? 'planned' : chosen;
  const [rows, setRows] = useState<Poi[]>([]);
  const [more, setMore] = useState(false);
  /** Every place the current search matches, not just the pages read. */
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [picked, setPicked] = useState<Poi | null>(null);

  /* What the catalogue says about the place on the right. */
  const [coverings, setCoverings] = useState<Covering[] | null>(null);


  /*
    How often every place in the city has been planned, keyed by id.

    One call for the whole catalogue rather than a coverage read per page:
    the figure beside a name, the rings the map draws filled and the
    new/planned filter are three uses of the same fact, and asking three
    times would be three ways for them to disagree.
  */
  const [tallies, setTallies] = useState<Map<string, PlaceTally> | null>(null);

  /*
    The map, or the record.

    Both answer "should I take this one?" and neither replaces the other: a
    place with a thin record may still be half an hour off the route, which
    only the map says. The map opens first because it is the one that works
    before anything is picked — it shows where the worked ground is, which
    is the shape of the question.
  */
  const [view, setView] = useState<'record' | 'map' | 'details'>('map');

  /*
    Whether the map marks the ground already worked.

    Off while a round is being built, because the map is then about the
    round: somebody placing a stop by eye wants it clear of everything
    else, and the marks are one switch away when the question changes. On
    and unswitchable on the coverage page, where marked ground is the only
    thing the page is for — a switch that turns the screen off is not a
    choice worth offering.
  */
  const [markPlanned, setMarkPlanned] = useState(plannedOnly);

  /*
    Where the map is pointed, which is not the same as what is picked.

    Picking from the list means "show me this one", so the map goes there.
    Picking a ring on the map means "this one, that I am already looking
    at", and re-framing the view under the cursor would throw away the pan
    somebody just made to find it.
  */
  const [focus, setFocus] = useState<{ lat: number; lng: number } | null>(null);

  /*
    The ground the map is showing, grown a third beyond each edge.

    Marks are drawn for this rather than for the whole city: a place
    outside the view is a DOM node, a tooltip and a hit area that nobody
    can see, and a city worked for years would put thousands of them down
    to show the dozen on screen. The margin is what stops a short pan
    arriving in blank ground — marks settle on rest, not during the drag.

    Null until the map has come to rest once, which draws everything (to
    the ceiling above) rather than nothing: an empty map would be the
    worse guess.
  */
  const [inView, setInView] = useState<ViewBox | null>(null);

  /*
    Where the map was left.

    The map is unmounted while the record is being read — it is the same
    pane — so coming back to it would otherwise open on the whole city
    again, however far in somebody had worked. Kept in a ref rather than
    in state because nothing on screen depends on it until the map is
    built again, and a re-render per pan would be a poor trade.
  */
  const camera = useRef<{ lat: number; lng: number; zoom: number } | null>(null);

  /*
    And what the place itself is: photographs, rating, hours, address.

    Read in the pane rather than in a window over it. It used to be a
    button that opened the place panel on top of this one — a window over
    a window, with the catalogue and the map hidden behind both while
    somebody compared one place against another. Fetched only while the
    tab is being shown, because most places are passed over without
    anybody asking what is inside them.
  */
  const { details, error: detailsFailed } = usePlaceDetails(
    view === 'details' && picked ? picked.id : null,
  );

  const listRef = useRef<HTMLDivElement | null>(null);

  const keys = useMemo(() => kinds.map((k) => k.key), [kinds]);
  const asked = `${query}|${keys.join(',')}|${standing}`;

  /* Every place in the city that any saved plan stops at. */
  useEffect(() => {
    if (!active) return undefined;

    let current = true;
    setTallies(null);
    void api.placeTallies(city.id)
      .then((found) => { if (current) setTallies(found); })
      // A catalogue that cannot be marked up is still a catalogue: the
      // browser works without the figures, so a failure here is silent
      // rather than an alert over a list that is perfectly usable.
      .catch(() => { if (current) setTallies(new Map()); });

    return () => { current = false; };
  }, [active, city.id]);

  /*
    One search per question, debounced.

    Typing is a stream of questions and only the last one matters; without
    the wait every keystroke is a round trip, and the answers can arrive out
    of order and leave the list showing a prefix of what was typed.
  */
  useEffect(() => {
    if (!active) return undefined;
    /*
      Ground already worked is not read from the catalogue at all.

      The tallies are every planned place in the city, name and kind
      included, so that list is complete the moment they arrive — no
      paging, and no sifting of thousands of rows to find thirty.
    */
    if (standing === 'planned') return undefined;

    setLoading(true);
    setFailed(false);
    const timer = window.setTimeout(() => {
      void api.searchPois({
        cityId: city.id,
        q: query,
        categoryKeys: keys,
        visitableOnly: true,
        limit: PAGE,
        withTotal: true,
      })
        .then((found) => {
          setRows(found.pois);
          setMore(found.hasMore);
          setTotal(found.total);
        })
        .catch(() => setFailed(true))
        .finally(() => setLoading(false));
    }, 250);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, city.id, asked]);

  /*
    Back to the top whenever the question changes.

    A filter or a search answers with a different list, and leaving the
    scroll where it was drops the reader into the middle of one they have
    not seen the start of — which reads as the list having jumped.
  */
  useEffect(() => { listRef.current?.scrollTo({ top: 0 }); }, [asked]);

  /** Whether this one has been planned before, by the figures we hold. */
  const plansFor = useCallback(
    (poiId: string) => tallies?.get(poiId)?.plans ?? 0,
    [tallies],
  );

  /*
    The rows this filter admits.

    Two sources, because the two questions have two answers. "All" and
    "new" are the catalogue, read a page at a time and sifted against the
    figures. "Planned before" is the figures themselves: they hold every
    planned place in the city with its name and kind, so that list is
    whole as soon as they land, and narrowing it by name or by kind is
    done here rather than asked of a catalogue that would have to be read
    end to end to answer.
  */
  const shown = useMemo<Row[]>(() => {
    const line = (r: { id: string; name: string; categoryLabel: string }) => ({
      id: r.id, name: r.name, categoryLabel: r.categoryLabel,
    });

    if (standing === 'planned') {
      const wanted = query.trim().toLowerCase();
      const kindLabels = new Set(kinds.map((k) => k.label));

      return [...(tallies?.values() ?? [])]
        .filter((t) => (!wanted || t.name.toLowerCase().includes(wanted))
          // By label rather than by key: the tally carries what the place
          // is called, which is what the filter above is choosing from.
          && (kindLabels.size === 0 || kindLabels.has(t.categoryLabel)))
        .sort((a, b) => b.plans - a.plans || a.name.localeCompare(b.name))
        .map((t) => line({ id: t.poiId, name: t.name, categoryLabel: t.categoryLabel }));
    }

    const read = rows.map(line);

    return standing === 'new' && tallies !== null
      ? read.filter((r) => plansFor(r.id) === 0)
      : read;
  }, [rows, standing, tallies, plansFor, query, kinds]);

  /* The catalogue has more of it, and this list is reading the catalogue. */
  const canPage = more && standing !== 'planned';

  const loadMore = useCallback(() => {
    if (loading || !canPage) return;
    setLoading(true);
    void api.searchPois({
      cityId: city.id,
      q: query,
      categoryKeys: keys,
      visitableOnly: true,
      limit: PAGE,
      offset: rows.length,
    })
      .then((found) => {
        setRows((current) => [...current, ...found.pois]);
        setMore(found.hasMore);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [city.id, keys, loading, canPage, query, rows.length]);

  /* The record for whichever place is being read about. */
  useEffect(() => {
    if (!picked) return undefined;

    let current = true;
    setCoverings(null);
    void api.poiCoverage(city.id, [picked.id])
      .then((entries) => {
        if (current) setCoverings(entries[0]?.coverings ?? []);
      })
      .catch(() => { if (current) setCoverings([]); });

    return () => { current = false; };
  }, [city.id, picked]);

  /* Put away, or pointed at another city: either way, nothing is picked. */
  useEffect(() => {
    if (active) return;
    setPicked(null);
    setFocus(null);
  }, [active]);

  useEffect(() => {
    setPicked(null);
    setFocus(null);
  }, [city.id]);

  const roleOf = (poiId: string): string => {
    if (poiId === startId) return 'start';
    if (poiId === endId) return 'destination';

    return inRound.includes(poiId) ? 'in this round' : '';
  };

  /*
    Reading about one of them, wherever its line came from.

    Most rows are not pages this list has read: the map marks the whole
    city and "planned before" comes from the figures, so the place itself
    is fetched where it is not already in hand. `frame` says whether the
    map should go and look — it should when a name was chosen from the
    list, and it should not when the mark was clicked on the map, which
    would throw away the pan somebody just made to find it.
  */
  const pick = useCallback(async (poiId: string, frame: boolean) => {
    const known = rows.find((p) => p.id === poiId);
    const poi = known ?? await api.poi(poiId).catch(() => null);
    if (!poi) return;

    setPicked(poi);
    if (frame) setFocus({ lat: poi.lat, lng: poi.lng });
  }, [rows]);

  /*
    The markers: worked ground, all of it at once.

    Drawn from the tallies rather than from the rows on the left, which are
    one page of a catalogue of thousands. Taking them from the list meant
    marks appearing as somebody scrolled — a map that answered "what has
    been worked" with "whatever you have read so far", which is not the
    same question. The tally carries each place's name and position for
    exactly this.

    New ground is the list's business: it is most of the catalogue, and
    marking it tells nobody anything.
  */
  const pins = useMemo(() => {
    const byId = new Map<string, Marker>();

    if (markPlanned && tallies) {
      // In view first, then heaviest, so a cap cuts the ground planned
      // once rather than the ground planned eight times.
      const marked = [...tallies.values()]
        .filter((t) => (t.lat !== 0 || t.lng !== 0) && (!inView || holds(inView, t)))
        .sort((a, b) => b.plans - a.plans)
        .slice(0, MAP_PINS);

      for (const tally of marked) {
        byId.set(tally.poiId, {
          lat: tally.lat,
          lng: tally.lng,
          label: tally.name,
          poiId: tally.poiId,
          role: 'suggested',
          planned: tally.plans,
        });
      }
    }

    /* What the round makes of them, drawn as what they are. */
    for (const poi of rows) {
      const role = poi.id === startId ? ('start' as const)
        : poi.id === endId ? ('end' as const)
          : inRound.includes(poi.id) ? ('visit' as const)
            : null;
      if (!role) continue;

      byId.set(poi.id, {
        lat: poi.lat, lng: poi.lng, label: poi.name, poiId: poi.id, role,
      });
    }

    /* And the one being read about, which is never the one left off. */
    if (picked) {
      const already = byId.get(picked.id);
      byId.set(picked.id, already
        ? { ...already, highlighted: true }
        : {
          lat: picked.lat,
          lng: picked.lng,
          label: picked.name,
          poiId: picked.id,
          role: 'suggested',
          planned: plansFor(picked.id),
          highlighted: true,
        });
    }

    return [...byId.values()];
  }, [tallies, markPlanned, inView, rows, startId, endId, inRound, plansFor, picked]);

  /*
    What the map opens on: a place asked for, or where it was left.

    `focus` is a request — somebody picked a name on the left and the map
    should go there. It is cleared once honoured, below, so that from then
    on the map's own position is what comes back. Held still between
    renders either way: a fresh object every render is a fresh frame every
    render, which pins the view to one zoom and undoes every pan the
    moment anything else on the screen moves.
  */
  const focusPoint = useMemo(
    () => (focus ? { ...focus, zoom: 15 } : camera.current ?? undefined),
    // The camera is a ref, so the memo has to be told when to read it
    // again: on the way back to the map, which is the moment it matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [focus, view],
  );

  const workedHere = picked ? plansFor(picked.id) : 0;
  const lastPlanned = picked ? onDay(tallies?.get(picked.id)?.lastPlanDate ?? '') : '';
  /* A round to take a place into, or no round at all. */
  const takeable = Boolean(onSetStart || onSetEnd || onAddStop);

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} sx={{ height: '100%' }}>
      {/* The catalogue, narrowed by kind, by name and by standing. */}
      <Box
        sx={{
          width: { xs: '100%', md: 360 },
          flexShrink: 0,
          borderRight: { md: 1 },
          borderBottom: { xs: 1, md: 0 },
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 280, md: 0 },
        }}
      >
        <Stack spacing={1} sx={{ p: 1.5 }}>
          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a place by name"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Autocomplete
            multiple
            size="small"
            options={categories}
            value={kinds}
            onChange={(_event, next) => setKinds(next)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a.key === b.key}
            renderInput={(params) => (
              <TextField {...params} placeholder={kinds.length ? '' : 'All kinds of place'} />
            )}
          />

          {/*
            New ground, or ground already worked.

            The other half of why somebody opens this: a planner looking
            for somewhere fresh and a planner checking what the team
            already covers are reading the same catalogue for opposite
            reasons. Not offered where only one of them is the point —
            see plannedOnly.
          */}
          {!plannedOnly && (
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={standing}
            onChange={(_event, next: Standing | null) => { if (next) setChosen(next); }}
          >
            <ToggleButton value="all" sx={{ textTransform: 'none', py: 0.4 }}>
              All
            </ToggleButton>
            <ToggleButton
              value="new"
              disabled={tallies === null}
              sx={{ textTransform: 'none', py: 0.4 }}
            >
              New
            </ToggleButton>
            <ToggleButton
              value="planned"
              disabled={tallies === null}
              sx={{ textTransform: 'none', py: 0.4 }}
            >
              Planned before
            </ToggleButton>
          </ToggleButtonGroup>
          )}

          {/*
            How much there is, and how much of it is here.

            A list that pages has to say both, or "40 places" reads as the
            whole city. The planned list is whole as soon as it is drawn,
            so it says only its own size.
          */}
          <Typography variant="caption" color="text.secondary">
            {standing === 'planned'
              ? `${shown.length} ${shown.length === 1 ? 'place' : 'places'} planned before`
              : standing === 'new'
                ? `${shown.length} new of ${rows.length.toLocaleString()} read · ${total.toLocaleString()} places in all`
                : `${shown.length.toLocaleString()} of ${total.toLocaleString()} places`}
          </Typography>
        </Stack>

        <Divider />

        <Box ref={listRef} sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {failed && (
            <Alert severity="warning" variant="outlined" sx={{ m: 1.5 }}>
              The catalogue could not be read.
            </Alert>
          )}

          <List dense disablePadding>
            {shown.map((poi) => {
              const role = roleOf(poi.id);
              const plans = plansFor(poi.id);

              return (
                <ListItemButton
                  key={poi.id}
                  selected={picked?.id === poi.id}
                  onClick={() => { void pick(poi.id, true); }}
                >
                  <ListItemText
                    primary={poi.name}
                    secondary={[poi.categoryLabel, role].filter(Boolean).join(' · ')}
                    slotProps={{
                      primary: { noWrap: true },
                      secondary: { noWrap: true, variant: 'caption' },
                    }}
                  />
                  {/*
                    How often, in the same colour the map draws it.

                    Only on places that have been planned: "nothing yet"
                    beside nine names in ten is a column of noise, and
                    the absence of a figure says it already.
                  */}
                  {plans > 0 && (
                    <Tooltip
                      title={`In ${plans} saved plan${plans === 1 ? '' : 's'}${
                        tallies?.get(poi.id)?.lastPlanDate
                          ? ` · last planned for ${onDay(tallies.get(poi.id)?.lastPlanDate ?? '')}`
                          : ''
                      }`}
                    >
                      <Chip
                        size="small"
                        label={plans}
                        sx={{
                          ml: 1,
                          height: 20,
                          minWidth: 28,
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#fff',
                          bgcolor: COVERED_COLOUR,
                        }}
                      />
                    </Tooltip>
                  )}
                </ListItemButton>
              );
            })}
          </List>

          {loading && (
            <Stack sx={{ alignItems: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Stack>
          )}

          {!loading && shown.length === 0 && !failed && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {canPage
                ? 'Nothing matching in what has been read.'
                : standing === 'planned' && !query && kinds.length === 0
                  ? 'Nothing has been planned in this city yet.'
                  : 'Nothing here matches that.'}
            </Typography>
          )}

          {/*
            Asked for, rather than taken as somebody scrolls.

            Loading on scroll moved the ground under the reader: a page
            arriving while the list was being dragged shifted what was
            under the cursor, and a narrow filter fetched page after page
            on its own. A button pages when somebody wants it to, and the
            line above says how much is left to want.
          */}
          {canPage && !loading && (
            <Box sx={{ p: 1.5 }}>
              <Button fullWidth size="small" variant="outlined" onClick={loadMore}>
                {total > rows.length
                  ? `Load more · ${(total - rows.length).toLocaleString()} to go`
                  : 'Load more'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* What is known about the one being read about, and what to do with it. */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'flex-start', flexWrap: 'wrap' }}
            useFlexGap
          >
            <Box sx={{ flex: 1, minWidth: 160 }}>
              {picked ? (
                <>
                  <Typography variant="h3">{picked.name}</Typography>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    sx={{ mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}
                    useFlexGap
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      label={picked.categoryLabel}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                    {roleOf(picked.id) && (
                      <Chip
                        size="small"
                        color="primary"
                        label={roleOf(picked.id)}
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    )}
                    <Chip
                      size="small"
                      label={workedHere > 0
                        ? `In ${workedHere} saved plan${workedHere === 1 ? '' : 's'}${
                          lastPlanned ? ` · last for ${lastPlanned}` : ''}`
                        : 'Not planned before'}
                      sx={{
                        height: 20,
                        fontSize: 11,
                        ...(workedHere > 0
                          ? { bgcolor: COVERED_COLOUR, color: '#fff', fontWeight: 600 }
                          : {}),
                      }}
                      variant={workedHere > 0 ? 'filled' : 'outlined'}
                    />
                  </Stack>
                </>
              ) : (
                <>
                  <Typography variant="h3">{`Places in ${city.name}`}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {markPlanned
                      ? `The map marks the places plans already stop at, each carrying
                         how many${takeable ? ', alongside whatever is in the round' : ''}.
                         Click one to read its record.`
                      : takeable
                        ? `The map shows the round being built. Switch the marks back on
                           to see which ground has already been worked.`
                        : `Switch the marks back on to see which ground has already been
                           worked.`}
                  </Typography>
                </>
              )}
            </Box>

            {view === 'map' && !plannedOnly && (
              <FormControlLabel
                control={(
                  <Switch
                    size="small"
                    checked={markPlanned}
                    onChange={(e) => setMarkPlanned(e.target.checked)}
                  />
                )}
                label={(
                  <Typography variant="caption" color="text.secondary">
                    Mark planned places
                  </Typography>
                )}
                sx={{ mr: 0 }}
              />
            )}

            <ToggleButtonGroup
              exclusive
              size="small"
              value={view}
              onChange={(_event, next: 'record' | 'map' | 'details' | null) => {
                if (next) setView(next);
              }}
            >
              <ToggleButton value="map" sx={{ textTransform: 'none', px: 1 }}>
                <MapOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Map
              </ToggleButton>
              <ToggleButton value="record" sx={{ textTransform: 'none', px: 1 }}>
                <InsightsIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Coverage
              </ToggleButton>
              <ToggleButton
                value="details"
                disabled={!picked}
                sx={{ textTransform: 'none', px: 1 }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Details
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Box>

        {view === 'map' ? (
          <Box sx={{ flex: 1, minHeight: 240, position: 'relative', mt: 1.5 }}>
            <MapView
              city={city}
              plan={null}
              {...(basemapUrl ? { basemapUrl } : {})}
              pins={pins}
              {...(focusPoint ? { focusPoint } : {})}
              onCamera={(at) => {
                camera.current = at;
                /* And the ground to mark, which is the ground on screen. */
                setInView(wider(at.bounds));
                /* The request has been honoured; the map's own view now rules. */
                if (focus) setFocus(null);
              }}
              onPinClick={(poiId) => {
                /*
                  Most marks are not in the list: it has read a page, and
                  the map is marking the whole city. So a ring the list has
                  not reached is fetched rather than ignored — a marker
                  that does nothing when clicked is worse than no marker.
                */
                void pick(poiId, false);
              }}
            />
          </Box>
        ) : view === 'details' ? (
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pt: 1.5, pb: 2 }}>
            {picked
              ? <PlaceDetailsBody details={details} error={detailsFailed} />
              : (
                <Typography variant="body2" color="text.secondary">
                  Pick a place to read what is there.
                </Typography>
              )}
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pt: 1.5, pb: 2 }}>
            {!picked ? (
              <Typography variant="body2" color="text.secondary">
                Pick a place on the left — or a marker on the map — to see who has
                planned it, how often, and in which saved rounds.
              </Typography>
            ) : coverings === null ? (
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', py: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Reading what has been planned here…
                </Typography>
              </Stack>
            ) : (
              <PlaceCoverageReport coverings={coverings} resetKey={picked.id} />
            )}
          </Box>
        )}

        {/*
          The three things a place can be to a round.

          Kept together at the foot, because the decision is one
          decision — "take this, as what?" — and because a reader who
          has scrolled the record should not have to scroll back to
          act on it. Absent on the page that has no round in hand.
        */}
        {picked && takeable && (
          <>
            <Divider />
            <Stack
              direction="row"
              spacing={1}
              sx={{ p: 1.5, flexWrap: 'wrap' }}
              useFlexGap
            >
              {onSetStart && (
                <Tooltip title="Where the round sets off from">
                  <Button
                    size="small"
                    startIcon={<PlayArrowOutlinedIcon />}
                    onClick={() => onSetStart(picked)}
                    disabled={picked.id === startId}
                  >
                    Start here
                  </Button>
                </Tooltip>
              )}
              {onSetEnd && (
                <Tooltip title="Where the round finishes">
                  <Button
                    size="small"
                    startIcon={<FlagOutlinedIcon />}
                    onClick={() => onSetEnd(picked)}
                    disabled={picked.id === endId}
                  >
                    End here
                  </Button>
                </Tooltip>
              )}
              <Box sx={{ flex: 1 }} />
              {onAddStop && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddLocationAltOutlinedIcon />}
                  onClick={() => onAddStop(picked)}
                  disabled={inRound.includes(picked.id)
                    || picked.id === startId || picked.id === endId}
                >
                  {inRound.includes(picked.id) ? 'Already a stop' : 'Add as a stop'}
                </Button>
              )}
            </Stack>
          </>
        )}
      </Box>
    </Stack>
  );
}
